import { RadioGroupField, TypeaheadField } from '~/common/components';
import { useI18n, Trans, useApplicationContext } from '~/common/context';
import { CUSTOMER_LEVEL } from '@wyz/types';
import {
  Alert,
  Button,
  Card,
  CenteredSpinner,
  ColumnDefinition,
  Datatable,
  Icon,
  ISelectOption,
  Table,
  Text,
  Title,
} from '@wyz/ui';
import { Link } from 'react-router';
import { useMyCustomers } from '~/common/hooks';
import { IUserCustomer } from '~/common/types';
import { DUPLICATE_PP_MODAL_ACTION } from '~/purchase/schemas';
import { IDuplicatePolicyItem } from '~/purchase/types';
import { useManageUsers } from '~/common/api';

type ConflictedPoliciesTableProps = {
  customerId: string;
  conflictedPolicies: Array<IDuplicatePolicyItem>;
  isLoadingPolicies: boolean;
  customer: IUserCustomer;
};

function ConflictedPoliciesTable({
  customerId,
  conflictedPolicies,
  isLoadingPolicies,
  customer,
}: ConflictedPoliciesTableProps) {
  const { t } = useI18n();
  const getCustomersByUser = useMyCustomers();
  const { auth } = useApplicationContext();

  const getCustomerCurrency = (id: string) => {
    const customer = getCustomersByUser.data?.data.find(
      (customer) => customer.id === id,
    );
    return customer?.deliveryInformation.country.currencyId;
  };

  const manageFetchUsers = useManageUsers({
    customersByUserAutocomplete: { enabled: true },
  });

  const currentCustomerCurrency = getCustomerCurrency(customer.id);

  const isCurrencyDifferent = (idCustomer: string) => {
    return getCustomerCurrency(idCustomer) !== currentCustomerCurrency;
  };

  const transformOptionLabel = (option: ISelectOption): ISelectOption => {
    const currencyWarning = isCurrencyDifferent(option.value)
      ? ` ${t('app.purchase.redistribution.pricing_policies.customer.currency.duplicate.error')}`
      : '';

    return {
      ...option,
      label: `${option.label}${currencyWarning}`,
      disabled: isCurrencyDifferent(option.value),
    };
  };
  const columns: Array<ColumnDefinition<IDuplicatePolicyItem>> = [
    {
      field: 'customerName',
      sortable: true,
      headerName: (
        <Trans i18nKey='app.quote.pricing_policy.duplicate_modal.table.customer_name' />
      ),
      align: 'left',
      renderCell: (parameters) => {
        return (
          <div>
            {
              getCustomersByUser.data?.data.find(
                (customer) => customer.id === parameters.row.customerId,
              )?.name
            }
          </div>
        );
      },
    },
    {
      field: 'action',
      sortable: true,
      headerName: <Trans i18nKey='app.common.button.visit' />,
      align: 'left',
      renderCell: (parameters) => {
        return (
          <div className='d-flex justify-content-start'>
            <Button
              as={Link}
              to={`${parameters.row.id}/edit`}
              id={`view.${parameters.row.name}`}
              variant='link'
              size='sm'
              className='justify-content-end'
            >
              <Icon icon='eye' size='xxs' />
              <span>
                <Trans i18nKey='app.common.button.see' />
              </span>
            </Button>
          </div>
        );
      },
    },
  ];
  return (
    <>
      <Card>
        <Card.Body>
          <TypeaheadField
            name='customerIds'
            minCharacter={3}
            minCharacterMessage={
              <Trans
                i18nKey='app.common.autocomplete.min_character_message'
                values={{
                  minCharacter: 3,
                }}
              />
            }
            defaultOptionsFetcher={() =>
              manageFetchUsers
                .customersByUserAutocomplete('', {
                  excludedCustomers: [customerId],
                  userId: auth.userId as string,
                  minLevel: CUSTOMER_LEVEL.N1,
                  limit: 20,
                })
                .then((options) => options.map(transformOptionLabel))
            }
            label={
              <Trans i18nKey='app.quote.pricing_policy.duplicate_modal.form.customers' />
            }
            fetcher={(query) =>
              manageFetchUsers
                .customersByUserAutocomplete(query, {
                  excludedCustomers: [customerId],
                  userId: auth.userId as string,
                  minLevel: CUSTOMER_LEVEL.N1,
                  limit: 20,
                })
                .then((options) => options.map(transformOptionLabel))
            }
            wrapItems
            isMulti={true}
            required
            maxShown={Infinity}
          />
        </Card.Body>
      </Card>
      <Card>
        <Card.Body>
          {conflictedPolicies.length !== 0 && (
            <Alert variant='warning' className='d-flex align-items-center'>
              <Icon icon='alert' size='md' className='text-warning-text me-3' />
              <Text heading='sm' color='warning'>
                <Trans i18nKey='app.quote.pricing_policy.duplicate_modal.overlap_alert' />
              </Text>
            </Alert>
          )}
          <Table>
            <thead>
              <tr>
                {columns.map((column) => (
                  <th key={column.field}>{column.headerName}</th>
                ))}
              </tr>
            </thead>
            {isLoadingPolicies ? (
              <tbody>
                <tr>
                  <td colSpan={columns.length}>
                    <CenteredSpinner />
                  </td>
                </tr>
              </tbody>
            ) : (
              <tbody>
                {conflictedPolicies.map((item, itemIndex) => {
                  const rowId = `row-${item.name}`;

                  return (
                    <tr key={item.name}>
                      {columns.map((column) => {
                        return (
                          <Datatable.Cell
                            key={`${rowId}-${column.field}`}
                            dataItem={item}
                            column={column}
                            dataItemIndex={itemIndex}
                          />
                        );
                      })}
                    </tr>
                  );
                })}
              </tbody>
            )}
          </Table>
        </Card.Body>
      </Card>
      <Card>
        <Card.Body>
          <RadioGroupField
            name='action'
            label={
              <Title heading='lg' weight='bold'>
                <Trans i18nKey='app.quote.pricing_policy.duplicate_modal.form.action' />
              </Title>
            }
            options={[
              {
                label: t(
                  'app.quote.pricing_policy.duplicate_modal.form.save_as_draft',
                ),
                value: DUPLICATE_PP_MODAL_ACTION.SAVE_AS_DRAFT,
              },
              {
                label: t(
                  'app.quote.pricing_policy.duplicate_modal.form.save_as_apply',
                ),
                value: DUPLICATE_PP_MODAL_ACTION.SAVE_AND_APPLY,
              },
            ]}
            direction='vertical'
          />
        </Card.Body>
      </Card>
    </>
  );
}

export default ConflictedPoliciesTable;
